import { z } from "zod";
import { NOTES_MAX } from "@shared/constants/validation.js";

export const ROUTINE_LABEL_MAX = 120;
export const ROUTINE_TIME_MAX = 40;
export const ROUTINE_DETAILS_MAX = 1000;

export const ROUTINE_DAYPARTS = [
  "morning",
  "midday",
  "afternoon",
  "evening",
  "bedtime",
] as const;

export const ROUTINE_CATEGORIES = [
  "nap",
  "meal",
  "activity",
  "medication",
  "bedtime",
  "other",
] as const;

const nullableTrimmedString = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v && v.length > 0 ? v : null));

const updateNullableString = (max: number) =>
  z.string().trim().max(max).nullable().optional();

export const createRoutineSchema = z.object({
  label: z
    .string()
    .trim()
    .min(1, "Routine label is required")
    .max(ROUTINE_LABEL_MAX, "Routine label is too long"),
  daypart: z.enum(ROUTINE_DAYPARTS, {
    errorMap: () => ({ message: "Select a time of day" }),
  }),
  category: z.enum(ROUTINE_CATEGORIES, {
    errorMap: () => ({ message: "Select a category" }),
  }),
  timeText: nullableTrimmedString(ROUTINE_TIME_MAX),
  details: nullableTrimmedString(ROUTINE_DETAILS_MAX),
  sortOrder: z.number().int().min(0).max(100000).optional(),
  notes: nullableTrimmedString(NOTES_MAX),
});

export const updateRoutineSchema = z.object({
  label: z
    .string()
    .trim()
    .min(1, "Routine label is required")
    .max(ROUTINE_LABEL_MAX)
    .optional(),
  daypart: z.enum(ROUTINE_DAYPARTS).optional(),
  category: z.enum(ROUTINE_CATEGORIES).optional(),
  timeText: updateNullableString(ROUTINE_TIME_MAX),
  details: updateNullableString(ROUTINE_DETAILS_MAX),
  sortOrder: z.number().int().min(0).max(100000).optional(),
  notes: updateNullableString(NOTES_MAX),
});

export type CreateRoutineInput = z.infer<typeof createRoutineSchema>;
export type UpdateRoutineInput = z.infer<typeof updateRoutineSchema>;
